if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.verticalMql = window.matchMedia('(min-width: 990px)');
        this.hoverZoomMql = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
        this.hoverZoomFrame = null;
        this.hoverZoomGeometry = null;
        this.hoverZoomPointer = null;
        this.hoverZoomImageCache = new Map();
        this.thumbnailHoverDelay = 100;
        this.thumbnailHoverTimer = null;
        this.thumbnailHoverMediaId = null;
        this.initializeHoverZoom();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const mediaId = mediaToSwitch.dataset.target;
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;
          button.addEventListener('click', () => {
            this.cancelThumbnailHoverPreview();
            this.setActiveMedia(mediaId, false);
          });
          mediaToSwitch.addEventListener('pointerenter', () => {
            this.scheduleThumbnailHoverPreview(mediaToSwitch, mediaId);
          });
          mediaToSwitch.addEventListener('pointerleave', () => this.cancelThumbnailHoverPreview(mediaId));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      initializeHoverZoom() {
        this.querySelectorAll('[data-hover-zoom]').forEach((opener) => {
          const image = opener.querySelector('.product__media img');
          if (!image) return;

          opener.addEventListener('pointerenter', (event) => this.startHoverZoom(opener, image, event));
          opener.addEventListener('pointermove', (event) => this.moveHoverZoom(opener, image, event));
          opener.addEventListener('pointerleave', () => this.stopHoverZoom(opener));
          opener.addEventListener('pointercancel', () => this.stopHoverZoom(opener));
        });

        this.hoverZoomMql.addEventListener?.('change', () => {
          this.cancelThumbnailHoverPreview();
          this.resetHoverZoom();
        });
      }

      scheduleThumbnailHoverPreview(thumbnail, mediaId) {
        if (!this.hoverZoomMql.matches || thumbnail.querySelector('button')?.hasAttribute('aria-current')) return;

        this.cancelThumbnailHoverPreview();
        this.thumbnailHoverMediaId = mediaId;
        this.thumbnailHoverTimer = window.setTimeout(() => {
          this.thumbnailHoverTimer = null;
          this.thumbnailHoverMediaId = null;
          if (!this.hoverZoomMql.matches) return;
          this.setActiveMedia(mediaId, false, { playMedia: false, announce: false });
        }, this.thumbnailHoverDelay);
      }

      cancelThumbnailHoverPreview(mediaId) {
        if (mediaId && this.thumbnailHoverMediaId !== mediaId) return;
        if (this.thumbnailHoverTimer) window.clearTimeout(this.thumbnailHoverTimer);
        this.thumbnailHoverTimer = null;
        this.thumbnailHoverMediaId = null;
      }

      startHoverZoom(opener, image, event) {
        if (!this.hoverZoomMql.matches) return;

        const geometry = this.prepareHoverZoom(opener, image);
        if (!geometry) return;
        const source = opener.dataset.hoverZoomSrc;
        this.hoverZoomPointer = { opener, image, clientX: event.clientX, clientY: event.clientY };

        const cachedSource = this.hoverZoomImageCache.get(source);
        if (cachedSource?.state === 'loaded') {
          this.activateHoverZoom(opener, image, source);
          return;
        }

        opener.classList.add('is-hover-zoom-loading');
        opener.dataset.hoverZoomState = 'loading-high-resolution';
        this.loadHoverZoomSource(source)
          .then(() => {
            if (!this.hoverZoomMql.matches || this.hoverZoomPointer?.opener !== opener) return;
            this.activateHoverZoom(opener, image, source);
          })
          .catch(() => {
            opener.classList.remove('is-hover-zoom-loading', 'is-hover-zoomed');
            opener.dataset.hoverZoomState = 'disabled-source-load-error';
          });
      }

      moveHoverZoom(opener, image, event) {
        if (!this.hoverZoomMql.matches || !opener.classList.contains('anirona-hover-zoom-ready')) return;
        this.hoverZoomPointer = { opener, image, clientX: event.clientX, clientY: event.clientY };
        if (!opener.classList.contains('is-hover-zoomed')) return;
        this.queueHoverZoomPosition(opener, image, event.clientX, event.clientY);
      }

      stopHoverZoom(opener) {
        opener.classList.remove('is-hover-zoom-loading', 'is-hover-zoomed');
        if (this.hoverZoomGeometry?.opener === opener) this.hoverZoomGeometry = null;
        if (this.hoverZoomPointer?.opener === opener) this.hoverZoomPointer = null;
      }

      resetHoverZoom() {
        this.querySelectorAll('[data-hover-zoom]').forEach((opener) => {
          opener.classList.remove('anirona-hover-zoom-ready', 'is-hover-zoom-loading', 'is-hover-zoomed');
          opener.style.removeProperty('--anirona-hover-zoom-scale');
          opener.style.removeProperty('--anirona-hover-zoom-position-x');
          opener.style.removeProperty('--anirona-hover-zoom-position-y');
          opener.style.removeProperty('--anirona-hover-zoom-origin-x');
          opener.style.removeProperty('--anirona-hover-zoom-origin-y');
          opener.removeAttribute('data-hover-zoom-scale');

          const image = opener.querySelector('.product__media img');
          image?.style.removeProperty('transform');
          image?.style.removeProperty('transform-origin');

          const layer = opener.querySelector('.anirona-hover-zoom-layer');
          if (!layer) return;
          layer.style.removeProperty('left');
          layer.style.removeProperty('top');
          layer.style.removeProperty('width');
          layer.style.removeProperty('height');
          layer.style.removeProperty('background-image');
          layer.style.removeProperty('background-size');
        });
        this.hoverZoomGeometry = null;
        this.hoverZoomPointer = null;
        if (!this.hoverZoomFrame) return;
        cancelAnimationFrame(this.hoverZoomFrame);
        this.hoverZoomFrame = null;
      }

      prepareHoverZoom(opener, image) {
        const geometry = this.getHoverZoomGeometry(opener, image);
        if (!geometry || geometry.scale < 1.2) {
          opener.classList.remove('anirona-hover-zoom-ready', 'is-hover-zoom-loading', 'is-hover-zoomed');
          opener.removeAttribute('data-hover-zoom-scale');
          opener.dataset.hoverZoomState = geometry ? 'disabled-low-resolution' : 'disabled-invalid-geometry';
          return null;
        }

        if (!opener.dataset.hoverZoomSrc) {
          opener.classList.remove('anirona-hover-zoom-ready');
          opener.dataset.hoverZoomState = 'disabled-missing-source';
          return null;
        }

        opener.style.setProperty('--anirona-hover-zoom-scale', geometry.scale.toFixed(2));
        opener.dataset.hoverZoomScale = geometry.scale.toFixed(2);
        opener.dataset.hoverZoomState = 'ready-high-resolution';
        opener.classList.add('anirona-hover-zoom-ready');
        return geometry;
      }

      prepareActiveMediaHoverZoom(activeMedia) {
        if (!this.hoverZoomMql.matches) return;
        const opener = activeMedia.querySelector('[data-hover-zoom]');
        const image = opener?.querySelector('.product__media img');
        if (opener && image) this.prepareHoverZoom(opener, image);
      }

      queueHoverZoomPosition(opener, image, clientX, clientY) {
        this.hoverZoomPointer = { opener, image, clientX, clientY };
        if (this.hoverZoomFrame) return;

        this.hoverZoomFrame = requestAnimationFrame(() => {
          const pointer = this.hoverZoomPointer;
          this.hoverZoomFrame = null;
          if (!pointer || !pointer.opener.classList.contains('is-hover-zoomed')) return;

          const geometry =
            this.hoverZoomGeometry?.opener === pointer.opener
              ? this.hoverZoomGeometry.geometry
              : this.getHoverZoomGeometry(pointer.opener, pointer.image);
          if (!geometry) return;

          const positionX = Math.min(
            geometry.contentWidth,
            Math.max(0, pointer.clientX - geometry.contentLeft)
          );
          const positionY = Math.min(
            geometry.contentHeight,
            Math.max(0, pointer.clientY - geometry.contentTop)
          );
          const positionPercentX = (positionX / geometry.contentWidth) * 100;
          const positionPercentY = (positionY / geometry.contentHeight) * 100;

          pointer.opener.style.setProperty('--anirona-hover-zoom-position-x', `${positionPercentX.toFixed(2)}%`);
          pointer.opener.style.setProperty('--anirona-hover-zoom-position-y', `${positionPercentY.toFixed(2)}%`);
        });
      }

      activateHoverZoom(opener, image, source) {
        const geometry = this.getHoverZoomGeometry(opener, image);
        const layer = opener.querySelector('.anirona-hover-zoom-layer');
        if (!geometry || geometry.scale < 1.2 || !layer) return;

        layer.style.left = `${(geometry.contentLeft - geometry.rect.left).toFixed(2)}px`;
        layer.style.top = `${(geometry.contentTop - geometry.rect.top).toFixed(2)}px`;
        layer.style.width = `${geometry.contentWidth.toFixed(2)}px`;
        layer.style.height = `${geometry.contentHeight.toFixed(2)}px`;
        layer.style.backgroundImage = `url(${JSON.stringify(source)})`;
        layer.style.backgroundSize = `${(geometry.scale * 100).toFixed(2)}% ${(geometry.scale * 100).toFixed(2)}%`;

        opener.classList.remove('is-hover-zoom-loading');
        opener.classList.add('is-hover-zoomed');
        opener.dataset.hoverZoomState = 'enabled-high-resolution';
        this.hoverZoomGeometry = { opener, geometry };

        const pointer = this.hoverZoomPointer;
        if (pointer?.opener === opener) {
          this.queueHoverZoomPosition(opener, image, pointer.clientX, pointer.clientY);
        }
      }

      loadHoverZoomSource(source) {
        const cachedSource = this.hoverZoomImageCache.get(source);
        if (cachedSource) return cachedSource.promise;

        const highResolutionImage = new Image();
        highResolutionImage.decoding = 'async';
        const record = { image: highResolutionImage, state: 'loading', promise: null };
        record.promise = new Promise((resolve, reject) => {
          highResolutionImage.onload = async () => {
            try {
              await highResolutionImage.decode?.();
            } catch (error) {
              // The decoded image is still usable when decode() is unsupported or interrupted.
            }
            record.state = 'loaded';
            resolve(highResolutionImage);
          };
          highResolutionImage.onerror = () => {
            record.state = 'error';
            reject(new Error('Hover zoom source could not be loaded.'));
          };
          highResolutionImage.src = source;
        });
        this.hoverZoomImageCache.set(source, record);
        return record.promise;
      }

      getHoverZoomGeometry(opener, image) {
        const rect = image.getBoundingClientRect();
        const originalWidth = Number(opener.dataset.imageWidth) || image.naturalWidth;
        const originalHeight = Number(opener.dataset.imageHeight) || image.naturalHeight;
        if (!rect.width || !rect.height || !originalWidth || !originalHeight) return null;

        const styles = getComputedStyle(image);
        const paddingTop = parseFloat(styles.paddingTop) || 0;
        const paddingRight = parseFloat(styles.paddingRight) || 0;
        const paddingBottom = parseFloat(styles.paddingBottom) || 0;
        const paddingLeft = parseFloat(styles.paddingLeft) || 0;
        const availableWidth = Math.max(1, rect.width - paddingLeft - paddingRight);
        const availableHeight = Math.max(1, rect.height - paddingTop - paddingBottom);
        const fitRatio = Math.min(availableWidth / originalWidth, availableHeight / originalHeight);
        const contentWidth = originalWidth * fitRatio;
        const contentHeight = originalHeight * fitRatio;
        const sourcePixelsPerCssPixel = Math.min(originalWidth / contentWidth, originalHeight / contentHeight);
        const scale = Math.min(2.25, Math.floor(sourcePixelsPerCssPixel * 100) / 100);

        return {
          rect,
          scale,
          contentWidth,
          contentHeight,
          contentLeft: rect.left + paddingLeft + (availableWidth - contentWidth) / 2,
          contentTop: rect.top + paddingTop + (availableHeight - contentHeight) / 2,
        };
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const playMedia = options.playMedia !== false;
        const announce = options.announce !== false;
        this.resetHoverZoom();
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        if (playMedia) {
          this.playActiveMedia(activeMedia);
        } else {
          window.pauseAllMedia();
        }
        this.prepareActiveMediaHoverZoom(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (announce && activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.dataset.thumbnailAxis === 'vertical' && this.verticalMql.matches) {
          const rail = this.elements.thumbnails.slider;
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          const visibleTop = rail.scrollTop + 10;
          const visibleBottom = rail.scrollTop + rail.clientHeight - 10;

          if (thumbnailTop >= visibleTop && thumbnailBottom <= visibleBottom) return;

          rail.scrollTo({
            top: Math.max(0, thumbnailTop - 10),
            behavior: 'smooth',
          });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
